import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { 
    getAllPayments, 
    searchPayments
} from "../../api/AdminApi";

const PaymentManagementPage = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    
    // 검색 관련 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('id');
    const [isSearchMode, setIsSearchMode] = useState(false);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    
    // 탭 관련 상태
    const [activeTab, setActiveTab] = useState('info');

    const fetchPayments = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllPayments(page, 20);
            setPayments(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(false);
        } catch (error) {
            console.error('결제 목록 조회 실패:', error);
            alert('결제 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (page = 0) => {
        if (!searchQuery.trim()) {
            alert('검색어를 입력해주세요.');
            return;
        }
        
        setLoading(true);
        try {
            const response = await searchPayments(page, 20, searchQuery, searchField);
            setPayments(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(true);
        } catch (error) {
            console.error('결제 검색 실패:', error);
            alert('결제 검색에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (payment) => {
        setModalOpen(true);
        setActiveTab('info'); // 기본 탭으로 설정
        setSelectedPayment(payment);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    useEffect(() => {
        fetchPayments();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(page);
            } else {
                fetchPayments(page);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    const formatAmount = (amount) => {
        if (!amount) return '0';
        return amount.toLocaleString('ko-KR');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        handleSearch(0);
    };

    const handleReset = () => {
        setSearchQuery('');
        setCurrentPage(0);
        fetchPayments(0);
    };

    // 테이블 컬럼 정의
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'memberId', field: 'memberId', minWidth: 'min-w-16' },
        { header: 'transactionId', field: 'transactionId', minWidth: 'min-w-32' },
        { header: 'productId', field: 'productId', minWidth: 'min-w-20' },
        { header: 'currency', field: 'currency', minWidth: 'min-w-12' },
        { 
            header: 'amount', 
            field: 'amount', 
            minWidth: 'min-w-16',
            render: (payment) => formatAmount(payment.amount)
        },
        { header: 'platform', field: 'platform', minWidth: 'min-w-16' },
        { header: 'status', field: 'status', minWidth: 'min-w-16' },
        { 
            header: 'createdAt', 
            field: 'createdAt', 
            minWidth: 'min-w-32',
            render: (payment) => formatDateTime(payment.createdAt)
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">결제 관리</h1>
                
                {/* 검색 폼 */}
                <div className="mb-3 p-2 bg-gray-100 border border-gray-300">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 text-xs">
                        <div className="flex gap-2">
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="id" 
                                    checked={searchField === 'id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                ID
                            </label>
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="member_id" 
                                    checked={searchField === 'member_id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                멤버 ID
                            </label>
                        </div>
                        <input 
                            type="text" 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="검색어 입력"
                            className="px-2 py-1 border border-gray-300 text-xs"
                        />
                        <button type="submit" className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            검색
                        </button>
                        <button type="button" onClick={handleReset} className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            전체보기
                        </button>
                    </form>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}건 | {currentPage + 1}/{totalPages} 페이지 
                    {isSearchMode && ` | 검색: ${searchField}="${searchQuery}"`}
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={payments}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 결제 상세 모달 */}
                <InfoModal 
                    isOpen={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    title={`결제 정보 - ID: ${selectedPayment?.id || ''}`}
                    width="w-[600px]"
                >
                    {selectedPayment ? (
                        <div className="text-xs">
                            {/* 윈도우 2000 스타일 탭 */}
                            <div className="mb-4">
                                <div className="flex border-b border-gray-400">
                                    <button
                                        onClick={() => handleTabChange('info')}
                                        className={`px-3 py-2 text-xs border-l border-t border-r border-gray-400 ${
                                            activeTab === 'info' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        결제 정보
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('receipt')}
                                        className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                            activeTab === 'receipt' 
                                                ? 'bg-white border-b-white -mb-px relative z-10' 
                                                : 'bg-gray-200 border-b-gray-400'
                                        }`}
                                    >
                                        영수증 데이터
                                    </button>
                                </div>
                            </div>

                            {/* 탭 내용 */}
                            {activeTab === 'info' ? (
                                <div>
                                    <h3 className="font-bold mb-2 text-sm">결제 정보</h3>
                                    <table className="w-full border border-gray-400">
                                        <tbody>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.id}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">memberId</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.memberId}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">transactionId</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.transactionId}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">productId</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.productId}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">currency</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.currency}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">amount</td><td className="border border-gray-400 px-2 py-1">{formatAmount(selectedPayment.amount)}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">platform</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.platform}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">status</td><td className="border border-gray-400 px-2 py-1">{selectedPayment.status}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedPayment.createdAt)}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* 영수증 데이터 탭 */
                                <div>
                                    <h3 className="font-bold mb-2 text-sm">영수증 데이터</h3>
                                    <div className="border border-gray-400 p-3">
                                        <div className="bg-gray-50 p-2 rounded">
                                            <pre className="text-xs whitespace-pre-wrap break-all">
                                                {selectedPayment.receiptData || '영수증 데이터가 없습니다.'}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default PaymentManagementPage;